import { localizeWorkspaceError } from '@/lib/workspace-error-copy';
import { useWorkspaceText } from './interface-copy';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useCompany } from '@/company/context';
import type { ServerThreadMeta, SessionUser } from '@/server-threads';
import type { ThreadLiveState } from '@/lib/chat-follow';
import type { ThreadMeta } from '@/thread-types';
import { chatIdFromHash, writeWorkspaceHash } from '@/lib/chat-links';
import { useCanvasNavigation } from './use-canvas-navigation';
import type { ChatNavigationStatus } from '@/lib/use-chat-navigation';
import { MAX_UPLOAD_BYTES, UPLOAD_RULE, uploadMime, workspaceApi } from './api';
import { uploadWorkspaceFile, fileReadabilityMessage } from './file-upload';
import { skillContext, WORKSPACE_ATTACHMENT_LIMIT } from './skill-context';
import { useSelectedReferences, type ReferenceMetadata } from './selected-references';
import { useNavigationIntent } from './navigation-intent';
import type { CanvasNavigationOptions } from './canvas-navigation';
import {
  runAfterSettingsGuard,
  canonicalWorkspaceHash,
  isSettingsView,
  parseWorkspaceRoute,
  type LibraryTab,
} from './navigation';
import {
  emptyTurnContext,
  projectHomeContext,
  emptyWorkspace,
  type CanvasTarget,
  type CompanySkill,
  type KnowledgeTab,
  type WorkspaceData,
  type WorkspaceTurnContext,
  type WorkspaceView,
  type NativeSelectionAttachment,
  type CapturedNativeSelection,
} from './types';
import { useToastQueue, type ToastQueue, type ToastTone } from '@/components/ui/toast';
import type { ArtifactCanvasRenderer } from './artifacts/outlet';
import type { EditingAppSelection } from '../../src/apps/tracer/editing-base.mjs';
import {
  dropAppChatDraftNotFor,
  onAppChatDraftQueued,
  takeAppChatDraft,
} from './apps/app-chat-draft';

export interface WorkspaceState {
  artifactCanvas?: ArtifactCanvasRenderer;
  data: WorkspaceData;
  loading: boolean;
  error: string | null;
  user: SessionUser | null;
  companyName: string;
  threadId: string;
  /** The current chat has been saved or opened from server history. */
  threadSaved: boolean;
  chatStatus: ChatNavigationStatus;
  retryChat: () => void;
  threads: ThreadMeta[];
  view: WorkspaceView;
  canvas: CanvasTarget | null;
  canvasDirty: boolean;
  /** Unsaved edits in the view's own editor (Knowledge's full page), apart from the canvas's. */
  viewDirty: boolean;
  canvasReading: boolean;
  canvasLeavePrompt: boolean;
  canvasAutoOpened: boolean;
  canvasHasPrevious: boolean;
  canvasPrevious: CanvasTarget | null;
  /** The page the open canvas came from, or null on a direct link. */
  canvasOrigin: WorkspaceView | null;
  finderOpen: boolean;
  mobileOpen: boolean;
  compact: boolean;
  turnContext: WorkspaceTurnContext;
  contextReferences?: ReferenceMetadata;
  contextReferencesError?: string;
  notice: string;
  /** One follow-up the toast offers, such as "Make private" (PR 3 §4). */
  noticeAction: NoticeAction | null;
  /** The toast stack the shell renders (DESIGN-SPEC §6.12); `notice` is its newest. */
  toasts: ToastQueue;
  busy: boolean;
  knowledgeTab: KnowledgeTab;
  setKnowledgeTab: (tab: KnowledgeTab) => void;
  refresh: () => Promise<void>;
  /** `library` opens the Library page on its Artifacts tab (a route, so reload and Back keep it). */
  navigate: (view: WorkspaceView, library?: LibraryTab) => void;
  libraryTab: LibraryTab | undefined;
  /** The project whose home is open (a new chat in it), from the route. */
  projectId: string | undefined;
  /** Opens a project's home, a new chat in the project; null opens All projects. */
  openProject: (id: string | null) => void;
  returnToWork: () => void;
  openCanvas: (canvas: CanvasTarget | null, options?: CanvasNavigationOptions) => boolean;
  canvasBack: () => void;
  canvasHistory: ReturnType<typeof useCanvasNavigation>['canvasHistory'];
  setCanvasDirty: (dirty: boolean) => void;
  /** Report unsaved edits in the view's own editor; a docked canvas reports through setCanvasDirty. */
  setViewDirty: (dirty: boolean) => void;
  /** Run `leave` now, or, with unsaved edits, once the person chooses to
   * discard them in the shared leave dialog (for moves inside one page). */
  confirmLeave: (leave: () => void) => void;
  setCanvasReading: (reading: boolean) => void;
  setCanvasScroll: (scrollTop: number) => void;
  cancelCanvasLeave: () => void;
  discardCanvasLeave: () => void;
  canvasScrollTop: number;
  setFinderOpen: (value: boolean) => void;
  setMobileOpen: (value: boolean) => void;
  setCompact: (value: boolean) => void;
  setTurnContext: (context: WorkspaceTurnContext) => void;
  /** Capture locally before async submission; the genuine thread is mirrored before freeze. */
  captureNativeSelection: (threadId: string) => CapturedNativeSelection | null;
  /** Registration is an ephemeral host hint, never create/edit authority. */
  registerEditingApp: (
    canvasKey: string,
    selected: EditingAppSelection | null | undefined,
    gone?: boolean,
  ) => () => void;
  captureEditingApp: (threadId: string) => EditingAppSelection | null | undefined;
  /** A turn refused because the preview on show is gone: its message and that preview, so Continue without the preview
   * acts only there. `gone: false` (that message was sent after all) forgets it. */
  noteGoneRefusal: (threadId: string, messageId: string, gone: boolean) => void;
  /** Whether this message's gone-preview refusal still names the preview on show (the same canvas, still gone); with
   * `closed`, whether it still names this message once that preview has closed. Another selection shown since has
   * already ended it, so closing that one never counts. */
  goneRefusalHolds: (threadId: string, messageId: string, closed?: boolean) => boolean;
  send: (
    text: string,
    context?: WorkspaceTurnContext,
    submit?: boolean,
    canvas?: CanvasTarget | null,
    /** newChat: write into a new conversation unless this one is still empty. */
    options?: { newChat?: boolean; nativeSelection?: NativeSelectionAttachment },
  ) => void;
  /** Read the runtime's draft without changing its text or attachments. */
  hasComposerDraft?: () => boolean;
  newChat: () => void;
  openThread: (id: string) => void;
  /** Capture before async work; only its latest navigation intent may complete. */
  beginNavigation: () => () => boolean;
  authorSkill: (skill?: CompanySkill) => void;
  /** "Use in chat": picks the published Skill for a chat, which follows it when the person asks. */
  useSkill: (skill: CompanySkill) => void;
  upload: (
    files: FileList | File[],
    knowledge?: boolean,
    options?: { attach?: boolean },
  ) => Promise<void>;
  notify: (text: string, action?: NoticeAction | null, tone?: ToastTone) => void;
  acceptArtifact: (target: CanvasTarget) => void;
  acceptEvent: (event: {
    runId: string;
    skillId: string;
    revision: number;
    status: string;
    resultId?: string;
  }) => void;
  acceptSkill: (skill: CompanySkill, options?: { open?: boolean }) => void;
  /** The open chat's sharing and the person's access to it (collaboration
   * v1); null for an ordinary owned chat. */
  threadMeta: ServerThreadMeta | null;
  /** Someone else's chat, shared with this person: they read, never write. */
  readOnly: boolean;
  /** Re-read the open chat's meta, after its sharing changed. */
  refreshThreadMeta: () => void;
  /** What the open chat's readers poll (lib/chat-follow.ts): a running
   * answer, followers, a stopped answer; null when nobody watches it. */
  threadLive: ThreadLiveState | null;
  /** The open chat stopped being readable while it was open (made private). */
  threadGone: boolean;
  /** Re-read the person's own chat list (after a fork created one). */
  refreshThreads: () => void;
  /** The Recent chats popover (opened from the "made private" page too). */
  recentOpen: boolean;
  setRecentOpen: (open: boolean) => void;
}
export interface NoticeAction {
  label: string;
  run: () => void | Promise<void>;
}
const Context = createContext<WorkspaceState | null>(null);
/** For tests that need a narrow workspace around one component. */
export const WorkspaceContext = Context;
export const useOptionalWorkspace = () => useContext(Context);
export function useWorkspace() {
  const state = useOptionalWorkspace();
  if (!state) throw new Error('WorkspaceProvider is required');
  return state;
}

export function WorkspaceProvider({
  user,
  threadId,
  chatLinkId = null,
  chatStatus = 'ready',
  retryChat = () => {},
  threads,
  onNewChat,
  onOpenThread,
  onDraft,
  hasComposerDraft,
  enabled = true,
  threadMeta = null,
  refreshThreadMeta = () => {},
  threadLive = null,
  threadGone = false,
  onThreadsChanged = () => {},
  artifactCanvas,
  children,
}: {
  user: SessionUser | null;
  threadId: string;
  chatLinkId?: string | null;
  chatStatus?: ChatNavigationStatus;
  retryChat?: () => void;
  threads: ThreadMeta[];
  /** Starts a conversation and returns its id: Skill authoring writes into the
   * thread that is about to open, so the contract is total, not best-effort. */
  onNewChat: () => string;
  onOpenThread: (id: string) => void;
  onDraft: (text: string, context: WorkspaceTurnContext, send: boolean) => void;
  hasComposerDraft?: () => boolean;
  enabled?: boolean;
  threadMeta?: ServerThreadMeta | null;
  refreshThreadMeta?: () => void;
  threadLive?: ThreadLiveState | null;
  threadGone?: boolean;
  onThreadsChanged?: () => void;
  artifactCanvas?: ArtifactCanvasRenderer;
  children: ReactNode;
}) {
  const { text: uiText, rich: uiRich, locale } = useWorkspaceText();
  const company = useCompany();
  const [data, setData] = useState<WorkspaceData>(emptyWorkspace);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [route, setRoute] = useState(() =>
    parseWorkspaceRoute(canonicalWorkspaceHash(location.hash) ?? location.hash),
  );
  // A reloaded design link restores only an assistant reference. Consume it once
  // when the authenticated company is known; never carry a retained canvas into
  // a later thread/account/company. The server rechecks ownership every turn.
  const initialStorefront = useRef(
    route.view === 'chat' && route.canvas?.kind === 'storefront'
      ? { store: route.canvas.id, thread: chatIdFromHash(location.hash) }
      : null,
  );
  const [finderOpen, setFinderOpen] = useState(false);
  const [recentOpen, setRecentOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [canvasDirty, setCanvasDirty] = useState(false);
  const [viewDirty, setViewDirty] = useState(false);
  const [canvasReading, setCanvasReading] = useState(false);
  const [compact, setCompactState] = useState(() => {
    try {
      return localStorage.getItem('poneva.workspace.compact') === 'true';
    } catch {
      return false;
    }
  });
  const [turnContext, setContext] = useState<WorkspaceTurnContext>(emptyTurnContext);
  // Up to three toasts, each with its own tone and timer; the same message twice
  // refreshes instead of stacking, and an empty text clears them.
  const toasts = useToastQueue();
  const notice = toasts.latest?.text ?? '';
  const notify = useCallback(
    (text: string, action?: NoticeAction | null, tone?: ToastTone) =>
      toasts.notify(localizeWorkspaceError(text, locale), action, tone),
    [toasts.notify, locale],
  );
  const [busy, setBusy] = useState(false);
  const [knowledgeTab, setKnowledgeTab] = useState<KnowledgeTab>('pages');
  const contexts = useRef(new Map<string, WorkspaceTurnContext>());
  const nativeSelections = useRef(new Map<string, NativeSelectionAttachment>());
  const editingApp = useRef<{
    canvasKey: string;
    gone: boolean;
    selected: EditingAppSelection | null | undefined;
  } | null>(null);
  // Collaboration v1: meta only for the chat that is open.
  const openMeta = threadMeta?.id === threadId ? threadMeta : null;
  const readOnly = openMeta?.access === 'viewer';
  const origin = useRef<{ view: WorkspaceView; canvas: CanvasTarget | null } | null>(null);
  const generation = useRef(0);
  const refreshGeneration = useRef(0);
  const scope = JSON.stringify([enabled, user?.id, company.data?.company?.id]);
  const navigationIntent = useNavigationIntent(scope);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const currentThread = useRef(threadId);
  currentThread.current = threadId;
  const currentReadOnly = useRef(readOnly);
  currentReadOnly.current = readOnly;
  const refresh = useCallback(async () => {
    if (currentScope.current !== scope) return;
    if (!enabled || !user || !company.data?.company) {
      setLoading(false);
      return;
    }
    const request = ++refreshGeneration.current;
    setLoading(true);
    setError(null);
    try {
      const workspace = await workspaceApi.list();
      if (currentScope.current !== scope || request !== refreshGeneration.current) return;
      setData(workspace);
    } catch (failure) {
      if (currentScope.current === scope && request === refreshGeneration.current)
        setError(
          failure instanceof Error ? failure.message : uiText('We could not load your workspace.'),
        );
    } finally {
      if (currentScope.current === scope && request === refreshGeneration.current)
        setLoading(false);
    }
  }, [scope]);
  useEffect(() => {
    generation.current += 1;
    refreshGeneration.current += 1;
    contexts.current.clear();
    nativeSelections.current.clear();
    setData(emptyWorkspace());
    const initial = emptyTurnContext();
    if (enabled && user && company.data?.company && initialStorefront.current) {
      if (
        route.view === 'chat' &&
        route.canvas?.kind === 'storefront' &&
        route.canvas.id === initialStorefront.current.store &&
        chatIdFromHash(location.hash) === initialStorefront.current.thread
      ) {
        initial.storefrontTarget = { store: initialStorefront.current.store };
        // Company hydration can finish before the URL's saved thread resolves.
        // Keep its reference under that destination, never the temporary thread.
        contexts.current.set(initialStorefront.current.thread ?? threadId, initial);
      }
      initialStorefront.current = null;
    }
    setContext(contexts.current.get(threadId) ?? emptyTurnContext());
    setRoute((current) =>
      current.canvas?.kind === 'voice-preview' ? { ...current, canvas: null } : current,
    );
    setFinderOpen(false);
    setBusy(false);
    notify('');
    void refresh();
    return () => {
      generation.current += 1;
      refreshGeneration.current += 1;
    };
  }, [refresh]);
  useEffect(() => {
    setContext(contexts.current.get(threadId) ?? emptyTurnContext());
    setRoute((current) =>
      current.canvas?.kind === 'voice-preview' ? { ...current, canvas: null } : current,
    );
  }, [threadId]);
  const selectedSkill = data.skills.find((skill) => skill.id === turnContext.intent.skillId);
  const detailReady = typeof selectedSkill?.draft?.instructions === 'string';
  useEffect(() => {
    const id = turnContext.intent.skillId;
    if (!enabled || !user || !company.data?.company || !id || detailReady) return;
    let active = true;
    void workspaceApi
      .skill(id)
      .then((skill) => {
        if (!active || currentScope.current !== scope) return;
        setData((current) => ({
          ...current,
          skills: [...current.skills.filter((item) => item.id !== id), skill],
        }));
      })
      .catch(() => {
        if (active && currentScope.current === scope)
          notify(uiText('The selected Skill is unavailable. Check its access or choose it again.'));
      });
    return () => {
      active = false;
    };
  }, [scope, turnContext.intent.skillId, turnContext.intent.revision, detailReady]);
  const references = useSelectedReferences(scope, turnContext, selectedSkill);
  const canvasNavigation = useCanvasNavigation({
    route,
    setRoute,
    enabled,
    chatLinkId,
    threadLink: () => (threadInUse() ? threadId : null),
    canvasDirty,
    setCanvasDirty,
    viewDirty,
    setViewDirty,
    canvasReading,
    setCanvasReading,
    setMobileOpen,
    navigationIntent,
  });
  // Every exit (the sidebar, New chat, Recent chats, page actions, Go back)
  // passes the one leave guard: Settings' own discard question, then the
  // unsaved-edit question when the move would unmount an editor.
  const { writeRoute, runAfterLeaveGuard } = canvasNavigation;
  const openCanvas = (canvas: CanvasTarget | null, options?: CanvasNavigationOptions) => {
    if (
      canvas?.kind === 'voice-preview' &&
      (canvas.threadId !== threadId ||
        currentScope.current !== scope ||
        currentThread.current !== threadId)
    )
      return false;
    return canvasNavigation.openCanvas(canvas, options);
  };
  // A project's home is the chat home in the project: a new chat that starts there. The chat
  // being left keeps its own context, as New chat leaves it.
  const openProject = (id: string | null) => {
    if (!id) {
      navigate('projects');
      return;
    }
    const next = { view: 'chat' as const, canvas: null, project: id };
    if (route.view === 'chat' && route.project === id && !route.canvas && !threadInUse()) {
      setMobileOpen(false);
      return;
    }
    runAfterLeaveGuard(next, () => {
      navigationIntent.cancel();
      setMobileOpen(false);
      setFinderOpen(false);
      if (!threadInUse()) {
        // A chat with nothing sent yet becomes the project's home as it is: what is typed in
        // its composer stays, and so do the files, results and Skill chosen for it. A chat in
        // use is left as New chat leaves it.
        const context = projectHomeContext(id, turnContext);
        contexts.current.set(threadId, context);
        setContext(context);
        writeRoute(next, null);
        return;
      }
      const context = projectHomeContext(id);
      const before = location.hash;
      const thread = onNewChat();
      contexts.current.set(thread, context);
      setContext(context);
      // Replace only the entry that starting the chat pushed, so the home is one step Back.
      writeRoute(next, null, location.hash !== before ? 'replace' : 'push');
    });
  };
  const navigate = (view: WorkspaceView, library?: LibraryTab) => {
    runAfterLeaveGuard({ view, canvas: null, library }, () => {
      if (isSettingsView(view) && !isSettingsView(route.view)) origin.current = route;
      writeRoute({ view, canvas: null, library });
      setMobileOpen(false);
    });
  };
  useEffect(() => {
    if (!enabled) return;
    const key = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setFinderOpen((value) => !value);
      }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [enabled]);
  const setTurnContext = (next: WorkspaceTurnContext) => {
    if (currentScope.current !== scope || currentThread.current !== threadId) return;
    contexts.current.set(threadId, next);
    setContext(next);
  };
  const captureNativeSelection = (destinationThread: string): CapturedNativeSelection | null => {
    const owner = generation.current;
    const requireOwner = () => {
      if (
        currentScope.current !== scope ||
        generation.current !== owner ||
        currentThread.current !== destinationThread ||
        destinationThread !== threadId ||
        currentReadOnly.current
      )
        throw new DOMException('Session changed', 'AbortError');
    };
    requireOwner();
    const attachment = nativeSelections.current.get(destinationThread);
    if (!attachment) return null;
    const freeze = attachment.capture();
    if (!freeze) {
      nativeSelections.current.delete(destinationThread);
      return null;
    }
    return {
      freeze: async () => {
        requireOwner();
        const result = await freeze({ kind: 'thread', id: destinationThread });
        requireOwner();
        if (nativeSelections.current.get(destinationThread) === attachment)
          nativeSelections.current.delete(destinationThread);
        return result;
      },
    };
  };
  const goneRefusal = useRef<{ threadId: string; messageId: string; canvasKey: string } | null>(
    null,
  );
  const registerEditingApp = useCallback(
    (canvasKey: string, selected: EditingAppSelection | null | undefined, gone = false) => {
      const binding = {
        canvasKey,
        gone,
        selected:
          selected == null
            ? selected
            : {
                artifactId: selected.artifactId,
                versionId: selected.versionId,
                revisionId: selected.revisionId,
              },
      };
      editingApp.current = binding;
      // Any other selection on show (another App, or this one no longer gone) ends a gone preview's refusal: its
      // Continue without the preview never acts after that, nor once that other selection has closed.
      const refusal = goneRefusal.current;
      if (refusal && (refusal.canvasKey !== canvasKey || !gone || selected !== undefined))
        goneRefusal.current = null;
      return () => {
        if (editingApp.current === binding) editingApp.current = null;
      };
    },
    [],
  );
  const noteGoneRefusal = (destinationThread: string, messageId: string, gone: boolean) => {
    const target = route.canvas;
    if (gone && target)
      goneRefusal.current = {
        threadId: destinationThread,
        messageId,
        canvasKey: `${target.kind}:${target.id}`,
      };
    else if (goneRefusal.current?.messageId === messageId) goneRefusal.current = null;
  };
  const goneRefusalHolds = (destinationThread: string, messageId: string, closed = false) => {
    const refusal = goneRefusal.current;
    if (
      !refusal ||
      refusal.threadId !== destinationThread ||
      destinationThread !== threadId ||
      refusal.messageId !== messageId
    )
      return false;
    if (closed) return true;
    const key = `${route.canvas?.kind}:${route.canvas?.id}`;
    const bound = editingApp.current;
    return (
      key === refusal.canvasKey &&
      bound?.canvasKey === key &&
      bound.gone &&
      bound.selected === undefined
    );
  };
  const captureEditingApp = (destinationThread: string): EditingAppSelection | null | undefined => {
    if (
      currentScope.current !== scope ||
      currentThread.current !== destinationThread ||
      destinationThread !== threadId ||
      currentReadOnly.current
    )
      throw new DOMException('Session changed', 'AbortError');
    const selected = editingApp.current;
    const target = route.canvas;
    // Closing or leaving a canvas does not request a new App. Missing capture
    // stays unselected; only an accepted new-work descriptor supplies null.
    if (!selected || selected.canvasKey !== `${target?.kind}:${target?.id}`) return undefined;
    // A preview that is gone (refused, nothing on show) is said apart from an App the chat cannot name: the person
    // may continue without it. Neither becomes a fresh App on its own.
    if (selected.selected === undefined)
      throw new Error(selected.gone ? 'editing_preview_unavailable' : 'editing_app_unavailable');
    return selected.selected === null ? null : { ...selected.selected };
  };
  // A conversation becomes real work the moment its first message is saved:
  // that id enters the URL and joins Recent chats.
  const threadInUse = () =>
    chatLinkId === threadId || threads.some((thread) => thread.id === threadId);
  // The route says whether a new chat is a project's home, both ways: a link, a reload or Back
  // to a project's home starts the chat in the project, and Back to the plain home takes it out.
  useEffect(() => {
    if (!enabled || route.view !== 'chat' || threadInUse()) return;
    // This chat's own context: right after a thread change (Back or Forward from a saved chat),
    // `turnContext` is still the last chat's, and its chips must not follow.
    const own = contexts.current.get(threadId) ?? emptyTurnContext();
    if (route.project && own.projectId !== route.project)
      setTurnContext(projectHomeContext(route.project, own));
    else if (!route.project && own.projectId) {
      const { projectId: _left, ...rest } = own;
      setTurnContext(rest);
    }
  }, [enabled, route.view, route.project, threadId, turnContext.projectId]);
  // An App's chat opened by "Fix in chat" takes its waiting draft once it is the open chat: placed, never sent.
  const placeDraft = useRef(onDraft);
  placeDraft.current = onDraft;
  useEffect(() => {
    const place = () => {
      const text = takeAppChatDraft(threadId);
      if (text) placeDraft.current(text, emptyTurnContext(), false);
    };
    place();
    // A draft still waiting for another chat when this one opens is abandoned: it never lands later.
    dropAppChatDraftNotFor(threadId);
    return onAppChatDraftQueued(place);
  }, [threadId]);
  const startTurn = ({
    text,
    context,
    submit,
    canvas,
    freshThread = false,
    nativeSelection,
  }: {
    text: string;
    context: WorkspaceTurnContext;
    submit: boolean;
    canvas: CanvasTarget | null;
    freshThread?: boolean;
    nativeSelection?: NativeSelectionAttachment;
  }) => {
    // A new chat started in a project keeps the project's home in its link until it is sent.
    const next = {
      view: 'chat' as const,
      canvas: canvas ? { ...canvas, full: false } : null,
      ...(context.projectId ? { project: context.projectId } : {}),
    };
    runAfterLeaveGuard(next, () => {
      // A new conversation is started before anything is written into it, so
      // the intent, canvas and draft land on the thread that will be open and
      // the one being left keeps its own. Starting it usually pushes the new
      // chat into history; when it did, the canvas route replaces that entry
      // rather than adding a second one, so the action is one step Back.
      const before = location.hash;
      // A reader never writes into someone else's chat: work they start from
      // a shared chat opens a chat of their own.
      const fresh = freshThread || readOnly;
      const thread = fresh ? onNewChat() : threadId;
      contexts.current.set(thread, context);
      if (nativeSelection) nativeSelections.current.set(thread, nativeSelection);
      else nativeSelections.current.delete(thread);
      setContext(context);
      writeRoute(
        next,
        fresh ? null : chatLinkId,
        // Replace only the entry that starting the conversation pushed.
        fresh && location.hash !== before ? 'replace' : 'push',
      );
      setMobileOpen(false);
      setFinderOpen(false);
      onDraft(text, context, submit);
    });
  };
  // Page actions start ordinary chat; Skill actions must supply their explicit intent.
  const send = (
    text: string,
    context = emptyTurnContext(),
    submit = false,
    canvas = route.canvas,
    options: { newChat?: boolean; nativeSelection?: NativeSelectionAttachment } = {},
  ) => {
    const freshThread = options.newChat === true && threadInUse();
    // Work started on a project's home stays in the project, as "Use in chat" on one of its files.
    const home = !freshThread && !threadInUse() ? route.project : undefined;
    const ordinary =
      context.intent.mode !== 'skill-author' &&
      context.intent.mode !== 'skill-preview' &&
      !context.newAgent &&
      !context.newProject;
    return startTurn({
      text,
      context: home && ordinary && !context.projectId ? { ...context, projectId: home } : context,
      submit,
      canvas,
      freshThread,
      nativeSelection: options.nativeSelection,
    });
  };
  // Authoring a Skill is its own work, never a layer over the conversation
  // already open. It joins this thread only while the thread is still empty, or
  // when the thread is already this Skill's own (its authoring, preview or run
  // conversation, including the one the Skill was just saved from).
  const rememberSkill = (skill: CompanySkill) => {
    if (currentScope.current !== scope) return;
    setData((current) => ({
      ...current,
      skills: [...current.skills.filter((item) => item.id !== skill.id), skill],
    }));
  };
  const authorSkill = (skill?: CompanySkill) => {
    if (skill) rememberSkill(skill);
    const next: WorkspaceTurnContext = skill
      ? skillContext(skill, 'skill-author')
      : { ...emptyTurnContext(), intent: { mode: 'skill-author' } };
    const continues = skill ? turnContext.intent.skillId === skill.id : false;
    startTurn({
      text: skill ? `Help me improve “${skill.draft.name}”.` : '',
      context: next,
      submit: false,
      canvas: { kind: 'skill', id: skill?.id ?? 'new' },
      freshThread: threadInUse() && !continues,
    });
  };
  // "Use in chat" picks the published Skill for a chat, as the composer's
  // Skill picker does: the person asks, and the answer follows the Skill. A
  // Skill never runs on its own and has no model of its own.
  const useSkill = (skill: CompanySkill) => {
    rememberSkill(skill);
    if (!skill.published) {
      authorSkill(skill);
      return;
    }
    const continues = turnContext.intent.skillId === skill.id;
    startTurn({
      text: '',
      context: skillContext(skill, 'skill-run'),
      submit: false,
      canvas: { kind: 'skill', id: skill.id },
      freshThread: threadInUse() && !continues,
    });
  };
  // attach: the upload started from the composer, so the saved files become
  // references on the message being written in this chat.
  // Files already saved stay attached and reported even when a later file fails.
  const attachSaved = (saved: string[], attach: boolean) => {
    const current = contexts.current.get(threadId) ?? turnContext;
    const fileIds = [...new Set([...current.fileIds, ...saved])];
    const attached = attach && saved.length > 0 && fileIds.length <= WORKSPACE_ATTACHMENT_LIMIT;
    if (attached) setTurnContext({ ...current, fileIds });
    return attached;
  };
  const upload = async (
    files: FileList | File[],
    includeInKnowledge = false,
    { attach = false }: { attach?: boolean } = {},
  ) => {
    if (busy || !files.length) return;
    const owner = generation.current;
    const saved: string[] = [];
    const warnings: string[] = [];
    setBusy(true);
    try {
      const selected = Array.from(files);
      if (selected.some((file) => !uploadMime(file.name) || file.size > MAX_UPLOAD_BYTES))
        throw new Error(UPLOAD_RULE);
      for (const file of selected) {
        if (owner !== generation.current) return;
        const stored = await uploadWorkspaceFile(file, includeInKnowledge);
        const unreadable = fileReadabilityMessage(stored);
        if (unreadable) warnings.push(unreadable);
        saved.push(stored.id);
      }
      if (owner === generation.current) {
        const attached = attachSaved(saved, attach);
        await refresh();
        if (owner !== generation.current) return;
        notify(
          warnings.length
            ? warnings.join(' ')
            : includeInKnowledge
              ? uiText('Source saved privately and included in your knowledge search.')
              : attached
                ? uiText('File saved privately to your library and attached to this message.')
                : uiText('File saved privately to your library.'),
        );
      }
    } catch (failure) {
      if (owner === generation.current) {
        const attached = attachSaved(saved, attach);
        const message =
          failure instanceof Error ? failure.message : uiText('We could not upload this file.');
        notify(
          saved.length
            ? uiText(
                attached
                  ? saved.length === 1
                    ? '{message} {count} earlier file was saved and attached.'
                    : '{message} {count} earlier files were saved and attached.'
                  : saved.length === 1
                    ? '{message} {count} earlier file was saved.'
                    : '{message} {count} earlier files were saved.',
                { message, count: saved.length },
              )
            : message,
        );
        await refresh();
      }
    } finally {
      if (owner === generation.current) setBusy(false);
    }
  };
  return (
    <Context.Provider
      value={
        enabled
          ? {
              artifactCanvas,
              data,
              loading,
              error,
              user,
              companyName: company.data?.company?.name ?? uiText('Your company'),
              threadId,
              threadSaved: threadInUse(),
              chatStatus,
              retryChat,
              threads,
              view: route.view,
              canvas: route.canvas,
              canvasDirty,
              viewDirty,
              canvasReading,
              canvasLeavePrompt: canvasNavigation.canvasLeavePrompt,
              canvasAutoOpened: canvasNavigation.canvasAutoOpened,
              canvasHasPrevious: canvasNavigation.canvasHasPrevious,
              canvasPrevious: canvasNavigation.canvasPrevious,
              canvasOrigin: canvasNavigation.canvasOrigin,
              finderOpen,
              mobileOpen,
              compact,
              turnContext,
              contextReferences: references.value,
              contextReferencesError: references.error,
              notice,
              noticeAction: toasts.latest?.action ?? null,
              toasts,
              busy,
              knowledgeTab,
              setKnowledgeTab,
              refresh,
              navigate,
              libraryTab: route.library,
              projectId: route.project,
              openProject,
              beginNavigation: navigationIntent.begin,
              hasComposerDraft,
              returnToWork: () => {
                const next = origin.current ?? { view: 'chat' as const, canvas: null };
                runAfterLeaveGuard(next, () => {
                  writeRoute(next);
                  setMobileOpen(false);
                });
              },
              openCanvas,
              canvasBack: canvasNavigation.canvasBack,
              canvasHistory: canvasNavigation.canvasHistory,
              setCanvasDirty: canvasNavigation.setCanvasDirty,
              setViewDirty: canvasNavigation.setViewDirty,
              confirmLeave: canvasNavigation.confirmLeave,
              setCanvasReading,
              setCanvasScroll: canvasNavigation.setCanvasScroll,
              canvasScrollTop: canvasNavigation.canvasScrollTop,
              cancelCanvasLeave: canvasNavigation.cancelCanvasLeave,
              discardCanvasLeave: canvasNavigation.discardCanvasLeave,
              setFinderOpen,
              setMobileOpen,
              setCompact: (value) => {
                setCompactState(value);
                try {
                  localStorage.setItem('poneva.workspace.compact', String(value));
                } catch {}
              },
              setTurnContext,
              captureNativeSelection,
              registerEditingApp,
              captureEditingApp,
              noteGoneRefusal,
              goneRefusalHolds,
              send,
              authorSkill,
              useSkill,
              upload,
              notify,
              acceptArtifact: (target) => {
                openCanvas(target);
                void refresh();
              },
              acceptEvent: (event) => {
                if (!event || typeof event.runId !== 'string' || typeof event.skillId !== 'string')
                  return;
                if (route.view === 'chat') openCanvas({ kind: 'skill', id: event.skillId });
                if (event.status !== 'running') {
                  if (
                    turnContext.intent.mode === 'skill-preview' &&
                    turnContext.intent.skillId === event.skillId
                  )
                    setTurnContext({
                      ...turnContext,
                      intent: {
                        mode: 'skill-author',
                        skillId: event.skillId,
                        revision: event.revision,
                      },
                    });
                  void refresh();
                }
              },
              threadMeta: openMeta,
              readOnly,
              refreshThreadMeta,
              threadLive: openMeta ? threadLive : null,
              threadGone: Boolean(openMeta) && threadGone,
              refreshThreads: onThreadsChanged,
              recentOpen,
              setRecentOpen,
              acceptSkill: (skill, options) => {
                setData((current) => ({
                  ...current,
                  skills: [...current.skills.filter((item) => item.id !== skill.id), skill],
                }));
                setTurnContext(skillContext(skill, 'skill-author', turnContext));
                if (options?.open && route.view === 'chat')
                  openCanvas({ kind: 'skill', id: skill.id });
              },
              // A new chat opens without a canvas.
              newChat: () => {
                runAfterLeaveGuard({ view: 'chat', canvas: null }, () => {
                  navigationIntent.cancel();
                  setMobileOpen(false);
                  setFinderOpen(false);
                  setContext(emptyTurnContext());
                  onNewChat();
                });
              },
              // Choosing the chat already open changes nothing; another opens
              // without a canvas.
              openThread: (id) => {
                const next =
                  route.view === 'chat' && id === threadId
                    ? route
                    : { view: 'chat' as const, canvas: null };
                runAfterLeaveGuard(next, () => {
                  navigationIntent.cancel();
                  setMobileOpen(false);
                  setFinderOpen(false);
                  onOpenThread(id);
                });
              },
            }
          : null
      }
    >
      {children}
    </Context.Provider>
  );
}
